import React from 'react';
import {
    Box, Text, HStack, Flex, Icon, Badge, Tag, Wrap, WrapItem, Switch, FormControl, FormLabel,
    Divider, Skeleton, Stack, Button,
} from '@chakra-ui/react';
import { FaLock, FaLockOpen, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { formatDateTime } from '../../utils/formatters';

const Linha = ({ rotulo, children }) => (
    <Box>
        <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            {rotulo}
        </Text>
        {children}
    </Box>
);

const textoDoSwitch = (bloqueio, desbloquear) => {
    if (desbloquear && bloqueio.por_imagem) {
        return { cor: 'green.700', icone: FaCheckCircle, texto: 'Recomendado: o bloqueio foi por foto divergente e as fotos novas resolvem o problema. O produto volta a ser vendido.' };
    }
    if (desbloquear) {
        return { cor: 'orange.700', icone: FaExclamationTriangle, texto: `Atenção: o bloqueio não é por imagem (${bloqueio.causa_rotulo}). Ao publicar, o produto volta a ser vendido.` };
    }
    if (bloqueio.por_imagem && bloqueio.outras_divergencias) {
        return { cor: 'gray.600', icone: FaLock, texto: 'Há outras divergências registradas além da foto. O produto continua bloqueado até que elas sejam resolvidas.' };
    }
    return { cor: 'gray.600', icone: FaLock, texto: 'O produto continua bloqueado. Só a imagem será atualizada.' };
};

const DesbloqueioProdutoCard = ({ carregando, erro, bloqueio, desbloquear, onChange, onTentarNovamente, desabilitado }) => {
    if (carregando) {
        return (
            <Stack p={3} spacing={2} borderRadius="md" borderWidth="1px" borderColor="gray.200" aria-busy="true">
                <Skeleton h="14px" w="45%" />
                <Skeleton h="12px" w="80%" />
                <Skeleton h="12px" w="60%" />
            </Stack>
        );
    }

    if (erro) {
        return (
            <Flex p={3} gap={3} borderRadius="md" borderWidth="1px" borderColor="gray.200" bg="gray.50" align={{ base: 'stretch', sm: 'center' }} direction={{ base: 'column', sm: 'row' }}>
                <HStack flex={1} align="start" spacing={2}>
                    <Icon as={FaExclamationTriangle} color="orange.400" mt="2px" flexShrink={0} />
                    <Text fontSize="xs" color="gray.700">
                        Não foi possível consultar o bloqueio do produto. Se publicar agora, o bloqueio não será alterado.
                    </Text>
                </HStack>
                <Button size="sm" variant="outline" onClick={onTentarNovamente} isDisabled={desabilitado} flexShrink={0}>
                    Tentar de novo
                </Button>
            </Flex>
        );
    }

    if (!bloqueio) return null;

    if (!bloqueio.bloqueado) {
        return (
            <HStack p={3} spacing={2} align="start" borderRadius="md" borderWidth="1px" borderColor="green.200" bg="green.50">
                <Icon as={FaLockOpen} color="green.500" mt="2px" flexShrink={0} />
                <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="green.800">Produto liberado para venda</Text>
                    <Text fontSize="xs" color="green.700">O produto não está bloqueado. Só a imagem será atualizada.</Text>
                </Box>
            </HStack>
        );
    }

    const aviso = textoDoSwitch(bloqueio, desbloquear);
    const desde = formatDateTime(bloqueio.bloqueado_em, null);

    return (
        <Box borderRadius="md" borderWidth="1px" borderColor="orange.200" bg="orange.50" overflow="hidden">
            <Stack p={3} spacing={3}>
                <Flex align="center" justify="space-between" gap={2} wrap="wrap">
                    <HStack spacing={2}>
                        <Icon as={FaLock} color="orange.500" />
                        <Text fontSize="sm" fontWeight="semibold" color="orange.800">Produto bloqueado</Text>
                    </HStack>
                    <Badge colorScheme={bloqueio.temporario ? 'yellow' : 'orange'} variant="subtle" borderRadius="full" px={2}>
                        {bloqueio.temporario ? 'Temporário' : 'Permanente'}
                    </Badge>
                </Flex>

                <Linha rotulo="Motivo do bloqueio">
                    <Text fontSize="sm" color="gray.800" fontWeight="medium">{bloqueio.causa_rotulo}</Text>
                    {Array.isArray(bloqueio.divergencias) && bloqueio.divergencias.length > 0 && (
                        <Wrap spacing={1} mt={1}>
                            {bloqueio.divergencias.map(item => (
                                <WrapItem key={item}>
                                    <Tag size="sm" borderRadius="full" colorScheme={item === 'Foto' ? 'purple' : 'red'} variant="subtle">
                                        {item === 'Foto' ? 'Foto divergente' : `${item} divergente`}
                                    </Tag>
                                </WrapItem>
                            ))}
                        </Wrap>
                    )}
                    {bloqueio.motivo && (
                        <Text fontSize="xs" color="gray.600" mt={1} wordBreak="break-word">{bloqueio.motivo}</Text>
                    )}
                </Linha>

                {(desde || bloqueio.bloqueado_por || bloqueio.origem) && (
                    <Text fontSize="xs" color="gray.600" wordBreak="break-word">
                        {desde ? `Desde ${desde}` : ''}
                        {bloqueio.bloqueado_por ? `${desde ? ' · ' : ''}por ${bloqueio.bloqueado_por}` : ''}
                        {bloqueio.origem ? `${desde || bloqueio.bloqueado_por ? ' · ' : ''}${bloqueio.origem}` : ''}
                    </Text>
                )}
            </Stack>

            <Divider borderColor="orange.200" />

            <Box p={3} bg="white">
                <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={3}>
                    <FormLabel htmlFor="desbloquear-produto" mb={0} fontSize="sm" fontWeight="semibold" color="gray.800" cursor="pointer">
                        Corrigir imagem e desbloquear o produto
                    </FormLabel>
                    <Switch
                        id="desbloquear-produto"
                        colorScheme="green"
                        size="lg"
                        isChecked={desbloquear}
                        onChange={(e) => onChange(e.target.checked)}
                        isDisabled={desabilitado}
                        flexShrink={0}
                    />
                </FormControl>
                <HStack spacing={2} align="start" mt={2}>
                    <Icon as={aviso.icone} color={aviso.cor} boxSize={3} mt="3px" flexShrink={0} />
                    <Text fontSize="xs" color={aviso.cor}>{aviso.texto}</Text>
                </HStack>
            </Box>
        </Box>
    );
};

export default DesbloqueioProdutoCard;
